import React, { useCallback, useEffect, useMemo, useState } from 'react';
import GenericDropdown from '../GenericDropdown';
import { API_BASE_URL, getAuthHeaders, previewTransform } from '../../utils/api';

async function fetchGroups(suffix) {
  const url = suffix
    ? `${API_BASE_URL}/casc/v1/api/data-review/groups?suffix=${encodeURIComponent(suffix)}&all=true`
    : `${API_BASE_URL}/casc/v1/api/data-review/groups?all=true`;
  const headers = await getAuthHeaders();
  const resp = await fetch(url, { method: 'GET', headers });
  if (!resp.ok) return [];
  const data = await resp.json();
  const list = Array.isArray(data?.groups) ? data.groups : [];
  return list.map((g) => ({
    id: g.group_slug || g.id || '',
    name: `${g.name || g.group_slug || ''} (${g.group_slug || ''})`,
  })).filter((g) => g.id);
}

async function fetchSampleRaw(suffix, groupSlug, limit = 5) {
  if (!suffix || !groupSlug) return [];
  const url =
    `${API_BASE_URL}/casc/v1/api/suffixes/${encodeURIComponent(suffix)}` +
    `/groups/${encodeURIComponent(groupSlug)}/entries?limit=${limit}&outputs=false&raw=true`;
  const headers = await getAuthHeaders();
  const resp = await fetch(url, { method: 'GET', headers });
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok || data?.success === false) return [];
  return Array.isArray(data?.data) ? data.data : [];
}

/** Collect dotted / json path hints from sample raw objects. */
function collectRawPaths(samples, maxDepth = 3) {
  const paths = new Set();
  const walk = (val, prefix, depth) => {
    if (depth > maxDepth || val == null) return;
    if (Array.isArray(val)) {
      if (prefix) paths.add(prefix);
      if (val.length && typeof val[0] === 'object' && val[0] !== null) {
        walk(val[0], prefix, depth + 1);
      }
      return;
    }
    if (typeof val === 'object') {
      for (const [k, v] of Object.entries(val)) {
        const next = prefix ? `${prefix}.${k}` : k;
        paths.add(next);
        if (v && typeof v === 'object') walk(v, next, depth + 1);
      }
    }
  };
  for (const row of samples || []) {
    const raw = row?.raw && typeof row.raw === 'object' ? row.raw : {};
    walk(raw, '', 0);
  }
  return [...paths].sort();
}

function pathToSqlAccessor(path) {
  const parts = String(path || '').split('.').filter(Boolean);
  if (parts.length === 0) return "raw->>'…'";
  if (parts.length === 1) return `raw->>'${parts[0]}'`;
  const nested = parts.slice(0, -1).map((p) => `->'${p}'`).join('');
  return `raw${nested}->>'${parts[parts.length - 1]}'`;
}

const TransformSqlConfig = ({
  value = {},
  onChange,
  suffix,
  groupSlug = null,
  excludeSlug = null,
}) => {
  const sources = useMemo(
    () => (Array.isArray(value.sources) ? value.sources : []),
    [value.sources]
  );
  const sql = value.sql || '';

  const [groupOptions, setGroupOptions] = useState([]);
  const [schemaByAlias, setSchemaByAlias] = useState({});
  const [schemaLoading, setSchemaLoading] = useState(false);
  const [previewRows, setPreviewRows] = useState(null);
  const [previewMeta, setPreviewMeta] = useState(null);
  const [previewError, setPreviewError] = useState('');
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    let active = true;
    fetchGroups(suffix).then((list) => {
      if (!active) return;
      const filtered = excludeSlug
        ? list.filter((g) => g.id !== excludeSlug)
        : list;
      setGroupOptions(filtered);
    });
    return () => { active = false; };
  }, [suffix, excludeSlug]);

  const emit = useCallback((patch) => {
    onChange?.({
      ...value,
      type: 'transform_sql',
      ...patch,
    });
  }, [onChange, value]);

  const updateSource = (idx, patch) => {
    const next = sources.map((s, i) => (i === idx ? { ...s, ...patch } : s));
    emit({ sources: next });
  };
  const addSource = () => {
    const n = sources.length + 1;
    emit({ sources: [...sources, { alias: `src${n}`, group_slug: '' }] });
  };
  const removeSource = (idx) => {
    emit({ sources: sources.filter((_, i) => i !== idx) });
  };

  const refreshSchema = useCallback(async () => {
    if (!suffix) return;
    setSchemaLoading(true);
    try {
      const next = {};
      for (const src of sources) {
        const alias = (src.alias || '').trim();
        const slug = (src.group_slug || '').trim();
        if (!alias || !slug) continue;
        const samples = await fetchSampleRaw(suffix, slug, 5);
        const paths = collectRawPaths(samples);
        next[alias] = {
          sampleCount: samples.length,
          paths,
          sample: samples[0]?.raw || null,
        };
      }
      setSchemaByAlias(next);
    } finally {
      setSchemaLoading(false);
    }
  }, [suffix, sources]);

  useEffect(() => {
    const ready = sources.some((s) => (s.alias || '').trim() && (s.group_slug || '').trim());
    if (!ready) {
      setSchemaByAlias({});
      return undefined;
    }
    const t = setTimeout(() => { refreshSchema(); }, 400);
    return () => clearTimeout(t);
  }, [sources, refreshSchema]);

  const runPreview = async () => {
    if (!suffix || !groupSlug) {
      setPreviewError('Save the SQL Transformation group first, then preview.');
      return;
    }
    setPreviewing(true);
    setPreviewError('');
    try {
      const data = await previewTransform(suffix, groupSlug, {
        config: {
          ...value,
          type: 'transform_sql',
          sources,
          sql,
        },
        limit: 100,
      });
      setPreviewRows(data.rows || []);
      setPreviewMeta({
        total: data.total,
        returned: data.returned != null ? data.returned : (data.rows || []).length,
        truncated: Boolean(data.truncated),
        counts: data.counts,
        columns: data.columns,
      });
    } catch (err) {
      setPreviewError(err.message || 'Preview failed');
      setPreviewRows(null);
      setPreviewMeta(null);
    } finally {
      setPreviewing(false);
    }
  };

  const previewColumns = useMemo(() => {
    if (previewMeta?.columns?.length) return previewMeta.columns;
    if (!previewRows || !previewRows.length) return [];
    const keys = new Set();
    previewRows.slice(0, 10).forEach((r) => {
      Object.keys(r.raw || {}).forEach((k) => keys.add(k));
    });
    return [...keys].slice(0, 20);
  }, [previewRows, previewMeta]);

  return (
    <div className="transform-sql-config">
      <div className="form-row">
        <label>Source groups</label>
        <div className="hint">
          Each source is a read-only SQLite table named by its alias. Fields live in the{' '}
          <code>raw</code> JSON column — use <code>raw-&gt;&gt;&apos;field&apos;</code>.
        </div>
        {sources.map((src, idx) => (
          <div key={idx} style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              type="text"
              value={src.alias || ''}
              onChange={(e) => updateSource(idx, { alias: e.target.value.trim() })}
              placeholder="alias"
              style={{ width: 120 }}
            />
            <div style={{ minWidth: 220, flex: 1 }}>
              <GenericDropdown
                items={groupOptions}
                value={src.group_slug || ''}
                onChange={(id) => updateSource(idx, { group_slug: id || '' })}
                placeholder="Select group"
                allowNone
                noneLabel="Select group"
              />
            </div>
            <button type="button" className="btn btn-secondary" onClick={() => removeSource(idx)}>Remove</button>
          </div>
        ))}
        <button type="button" className="btn btn-secondary" style={{ marginTop: 8 }} onClick={addSource}>
          Add source
        </button>
      </div>

      <div className="form-row">
        <label>Schema hints</label>
        <div className="hint">
          Sampled field paths from source groups (best-effort). Click a path to insert a SQL accessor.
          {schemaLoading ? ' Loading…' : ''}
        </div>
        {Object.keys(schemaByAlias).length === 0 && (
          <div className="hint">Assign sources with aliases to load field hints.</div>
        )}
        {Object.entries(schemaByAlias).map(([alias, info]) => (
          <div key={alias} style={{ marginTop: 8 }}>
            <strong>
              <code>{alias}</code>
            </strong>
            {' '}
            <span className="hint">({info.sampleCount} sample row(s))</span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
              {(info.paths || []).slice(0, 40).map((p) => {
                const snippet = pathToSqlAccessor(p);
                const qualified = `${alias}.${snippet}`;
                const colAlias = p.replace(/\./g, '_');
                return (
                  <button
                    key={p}
                    type="button"
                    className="btn btn-secondary"
                    style={{ fontSize: 11, padding: '2px 8px' }}
                    title={`Insert ${qualified}`}
                    onClick={() => {
                      emit({
                        sql: sql
                          ? `${sql}${sql.endsWith('\n') ? '' : '\n'}${qualified}`
                          : `SELECT ${qualified} AS ${colAlias} FROM ${alias}`,
                      });
                    }}
                  >
                    {p}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
        <button type="button" className="btn btn-secondary" style={{ marginTop: 8 }} onClick={refreshSchema} disabled={schemaLoading}>
          Refresh schema hints
        </button>
      </div>

      <div className="form-row">
        <label htmlFor="transform-sql-script">SQL script</label>
        <div className="hint">
          Multi-statement SQLite script. Sources are read-only. TEMP tables/views are allowed.
          The final statement must be a <code>SELECT</code> (or <code>WITH … SELECT</code>); its columns become the destination <code>raw</code> fields.
        </div>
        <textarea
          id="transform-sql-script"
          value={sql}
          onChange={(e) => emit({ sql: e.target.value })}
          rows={14}
          spellCheck={false}
          style={{ width: '100%', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', fontSize: 13 }}
          placeholder={"SELECT\n  raw->>'name' AS name,\n  CAST(raw->>'hours' AS REAL) AS hours\nFROM src"}
        />
      </div>

      <div className="form-row">
        <button type="button" className="btn btn-secondary" onClick={runPreview} disabled={previewing || !groupSlug}>
          {previewing ? 'Previewing…' : 'Preview SQL transform'}
        </button>
        {!groupSlug && (
          <div className="hint">Save the group first to enable preview. Horizon → Data Ingestion runs materialize.</div>
        )}
        {previewError && <div className="alert alert-error" style={{ marginTop: 8 }}>{previewError}</div>}
        {previewMeta && (
          <div className="hint" style={{ marginTop: 8 }}>
            {previewMeta.truncated
              ? `Preview truncated: showing ${previewRows?.length ?? previewMeta.returned ?? 0} of ${previewMeta.total} row(s)`
              : `${previewMeta.total} row(s)`}
            {previewMeta.counts ? ` (${Object.entries(previewMeta.counts).map(([k, v]) => `${k}:${v}`).join(', ')})` : ''}
          </div>
        )}
        {previewMeta?.truncated && (
          <div className="alert alert-warning" style={{ marginTop: 8 }}>
            Output exceeds the preview limit. The SQL ran successfully; only the first rows are shown so you can check structure and population. Full materialize on ingest still uses the complete result (up to the ingest output cap).
          </div>
        )}
        {previewRows && previewRows.length > 0 && (
          <div style={{ marginTop: 8, overflowX: 'auto', maxHeight: '28rem' }}>
            <table className="data-table" style={{ width: '100%', fontSize: 12 }}>
              <thead>
                <tr>
                  {previewColumns.map((c) => <th key={c}>{c}</th>)}
                </tr>
              </thead>
              <tbody>
                {previewRows.map((row, i) => (
                  <tr key={i}>
                    {previewColumns.map((c) => (
                      <td key={c}>{row.raw?.[c] == null ? '' : String(row.raw[c])}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default TransformSqlConfig;
